import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// --- CHART LIBRARIES ---
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

// --- ICONS ---
import {
  FaUserMd,
  FaUsers,
  FaHospital,
  FaMoneyBillWave,
  FaChartLine,
  FaShieldAlt,
  FaSignOutAlt,
  FaBell,
  FaUserCircle,
  FaSearch,
  FaCog,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSave,
  FaLock,
  FaCamera,
  FaEnvelope,
  FaPhone,
  FaBars,
  FaTimes,
  FaUserSecret
} from 'react-icons/fa';

// --- STYLES ---
// Make sure your src/styles/AdminDashboard.css contains the mobile styles provided earlier
import '../styles/AdminDashboard.css';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();

  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  
  // -- UI State --
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false); // Mobile Drawer Toggle

  // -- Data State --
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  // -- User Profile State --
  const [adminName, setAdminName] = useState("Super Admin");
  const [adminEmail, setAdminEmail] = useState("");

  // -- Dropdown Toggles --
  const [showNotif, setShowNotif] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  
  // -- Refs for Clicking Outside --
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  // -- Settings Form State --
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // ==========================================
  // 2. CONFIGURATION & HELPERS
  // ==========================================

  // Auth Header Generator
  const getConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // ==========================================
  // 3. INITIALIZATION & EFFECTS
  // ==========================================

  useEffect(() => {
    // A. Check Authorization
    const user = JSON.parse(localStorage.getItem('DDoctorUser'));
    if (!user || user.role !== 'super_admin') {
      navigate('/admin/login');
      return;
    }

    // B. Initialize Admin Data
    setAdminName(user.name || "Admin");
    setAdminEmail(user.email || "admin@ddoctor.pk");
    setSettingsForm(prev => ({
      ...prev,
      name: user.name,
      phone: user.phone || ""
    }));

    // C. Fetch Dashboard Data
    fetchData();

    // D. Setup Click Outside Listeners
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotif(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    
    // Cleanup
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []); // Run once on mount

  // ==========================================
  // 4. API ACTIONS
  // ==========================================

  // --- Fetch All Data ---
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Parallel API Calls for Efficiency
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/stats`, getConfig()),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/users`, getConfig())
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setLoading(false);

    } catch (err) {
      console.error("Dashboard Load Error:", err);
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate('/admin/login');
      } else {
        // Optional: Set some error state here if needed
        setLoading(false); 
      }
    }
  };

  // --- Handle Ban/Unban & Verify ---
  const handleAction = async (actionType, targetUser) => {
    // 1. Confirmation Dialog
    const confirmMsg = actionType === 'BAN_USER'
      ? `Are you sure you want to ${targetUser.isBanned ? 'UNBAN' : 'BAN'} this user?`
      : `Are you sure you want to ${actionType}?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      // 2. API Call
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/action`, {
        adminName,
        action: actionType,
        targetId: targetUser._id,
        details: `Manual Action by ${adminName}`
      }, getConfig());

      // 3. Refresh Data
      fetchData();
      alert("Action Completed Successfully.");

    } catch (err) {
      alert("Action Failed: " + (err.response?.data?.message || "Server Error"));
    }
  };

  // --- Handle Impersonation (Login As) ---
  const handleImpersonate = async (targetUser) => {
    const warningMsg = `⚠️ Security Warning\n\nYou are about to login as ${targetUser.name}.\nAll actions will be recorded.\n\nProceed?`;
    
    if (!window.confirm(warningMsg)) return;

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/impersonate`, {
        userId: targetUser._id,
        adminName: adminName
      }, getConfig());

      if (res.data.success) {
        // 1. Backup Admin Credentials
        localStorage.setItem('adminToken', localStorage.getItem('token'));
        localStorage.setItem('adminUser', localStorage.getItem('DDoctorUser'));

        // 2. Set Target User Credentials
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('DDoctorUser', JSON.stringify({
          ...targetUser,
          isImpersonated: true
        }));

        // 3. Redirect to User Dashboard
        if (targetUser.role === 'doctor') navigate('/doctor-dashboard');
        else if (targetUser.role === 'hospital') navigate('/hospital-dashboard');
        else navigate('/profile');
      }
    } catch (error) {
      alert("Impersonation Failed: " + (error.response?.data?.message || "Server Error"));
    }
  };

  // --- Handle Profile Update ---
  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    // Validation
    if (settingsForm.password && settingsForm.password !== settingsForm.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
        { 
          name: settingsForm.name, 
          password: settingsForm.password 
        },
        getConfig()
      );

      // Update Local Storage
      const updatedUser = { 
        ...JSON.parse(localStorage.getItem('DDoctorUser')), 
        name: res.data.user.name 
      };
      localStorage.setItem('DDoctorUser', JSON.stringify(updatedUser));

      // Update State
      setAdminName(res.data.user.name);
      alert("Profile Updated Successfully!");

      // Clear Sensitive Fields
      setSettingsForm(prev => ({ ...prev, password: '', confirmPassword: '' }));

    } catch (error) {
      alert("Update Failed: " + (error.response?.data?.message || error.message));
    }
  };

  // --- Handle Logout ---
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout of the Admin Console?")) {
      localStorage.clear();
      navigate('/admin/login');
    }
  };

  // --- Navigation Helper ---
  const handleNavClick = (tab) => {
    setActiveTab(tab);
    setShowSidebar(false); // Close sidebar on mobile when item clicked
  };

  // ==========================================
  // 5. SUB-COMPONENTS (RENDERERS)
  // ==========================================

  // --- A. Dashboard Overview (Home) ---
  const DashboardHome = () => (
    <>
      {/* KPI Cards Row */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Total Revenue</div>
          <div className="kpi-value text-primary">PKR {stats?.totalRevenue?.toLocaleString()}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Active Doctors</div>
          <div className="kpi-value">{stats?.totalDoctors}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Registered Patients</div>
          <div className="kpi-value">{stats?.totalUsers}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Memberships Sold</div>
          <div className="kpi-value text-purple">{stats?.totalCards}</div>
        </div>
      </div>

      {/* Charts & Logs Row */}
      <div className="row g-4 mb-4">
        {/* Line Chart */}
        <div className="col-lg-8 col-12">
          <div className="card border-0 shadow-sm p-3 h-100">
            <h6 className="fw-bold text-secondary mb-3">Revenue Overview (2026)</h6>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={[{ name: 'Jan', v: 4000 }, { name: 'Feb', v: 3000 }, { name: 'Mar', v: 5000 }, { name: 'Apr', v: 4500 }]}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="v" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Recent Logs Widget */}
        <div className="col-lg-4 col-12">
          <div className="card border-0 shadow-sm p-3 h-100">
            <h6 className="fw-bold text-secondary mb-3">Recent Security Logs</h6>
            <ul className="list-unstyled small">
              {stats?.recentLogs?.length > 0 ? stats.recentLogs.map((log, i) => (
                <li key={i} className="mb-3 border-bottom pb-2">
                  <span className="fw-bold text-dark">{log.action}</span>
                  <div className="text-muted" style={{ fontSize: '11px' }}>
                    {log.target} • {new Date(log.date).toLocaleDateString()}
                  </div>
                </li>
              )) : (
                <div className="text-muted text-center py-4">No recent logs found.</div>
              )}
            </ul>
          </div>
        </div>
      </div>
    </>
  );

  // --- B. Generic Data Table (For Users, Doctors, Hospitals) ---
  const DataTable = ({ data, type }) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Filtering Logic
    const filteredData = data.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="admin-table-container">
        {/* Table Header & Search */}
        <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white flex-wrap gap-2">
          <h5 className="fw-bold mb-0 text-dark">{type} Directory</h5>
          
          <div className="input-group" style={{ maxWidth: '300px', width: '100%' }}>
            <span className="input-group-text bg-light border-end-0">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control border-start-0 bg-light"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table Content */}
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name / ID</th>
                <th>Role</th>
                <th>Contact Info</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">
                    No records found matching "{searchTerm}"
                  </td>
                </tr>
              ) : (
                filteredData.map(item => (
                  <tr key={item._id}>
                    <td>
                      <div className="fw-bold text-dark">{item.name}</div>
                      <div className="text-muted small" style={{ fontSize: '10px' }}>
                        ID: {item._id.slice(-6)}
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border fw-normal">
                        {item.role.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <div>{item.email}</div>
                      <div className="text-muted small">{item.phone || "N/A"}</div>
                    </td>
                    <td>
                      {item.isBanned ? (
                        <span className="status-badge badge-banned">Banned</span>
                      ) : (
                        <span className="status-badge badge-active">Active</span>
                      )}
                      
                      {item.role === 'doctor' && !item.isVerified && (
                        <span className="status-badge badge-pending ms-1">Unverified</span>
                      )}
                    </td>
                    <td className="text-end" style={{ minWidth: '220px' }}>
                      {/* Login As Button */}
                      <button 
                        className="btn btn-sm btn-outline-dark btn-impersonate me-2" 
                        title="Login as User" 
                        onClick={() => handleImpersonate(item)}
                      >
                        <FaUserSecret className="me-1" /> Login As
                      </button>

                      {/* Verify Button (Doctors Only) */}
                      {type === 'Doctors' && !item.isVerified && (
                        <button 
                          className="action-btn text-success border-success me-2" 
                          onClick={() => handleAction('VERIFY_DOCTOR', item)}
                        >
                          Verify
                        </button>
                      )}

                      {/* Ban/Unban Button */}
                      <button 
                        className={`action-btn ${item.isBanned ? 'text-success' : 'text-danger'}`} 
                        onClick={() => handleAction('BAN_USER', item)}
                      >
                        {item.isBanned ? 'Unban' : 'Ban'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // --- C. Settings View (Oladoc Style) ---
  const SettingsView = () => (
    <div className="container-fluid px-0">
      <h4 className="fw-bold mb-4 text-dark">Profile Settings</h4>
      <div className="row g-4">
        
        {/* Left Column: Profile Card */}
        <div className="col-md-4">
          <div className="card border-0 shadow-sm text-center p-4 h-100">
            <div className="position-relative d-inline-block mx-auto mb-3">
              <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" 
                   style={{ width: '120px', height: '120px', border: '4px solid white', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                <FaUserCircle size={80} className="text-secondary" />
              </div>
              <div className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle p-2 border border-white" 
                   style={{ cursor: 'pointer' }}>
                <FaCamera size={14} />
              </div>
            </div>
            
            <h5 className="fw-bold mb-1">{adminName}</h5>
            <span className="badge bg-primary bg-opacity-10 text-primary mb-3">Super Administrator</span>

            <div className="text-start mt-4 px-3">
              <div className="d-flex align-items-center mb-3 text-muted small">
                <FaEnvelope className="me-3" /> {adminEmail}
              </div>
              <div className="d-flex align-items-center mb-3 text-muted small">
                <FaPhone className="me-3" /> +92 300 1234567
              </div>
              <div className="d-flex align-items-center text-muted small">
                <FaCheckCircle className="me-3 text-success" /> Account Verified
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Edit Form */}
        <div className="col-md-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white py-3">
              <h6 className="mb-0 fw-bold"><FaCog className="me-2" /> Edit Profile Information</h6>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleUpdateProfile}>
                
                {/* Name & Phone */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Full Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={settingsForm.name} 
                      onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })} 
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Phone Number</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={settingsForm.phone} 
                      placeholder="0300-0000000" 
                      onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })} 
                    />
                  </div>
                </div>

                {/* Email (Read Only) */}
                <div className="mb-3">
                  <label className="form-label small fw-bold">Email Address</label>
                  <input 
                    type="email" 
                    className="form-control bg-light" 
                    value={adminEmail} 
                    disabled 
                  />
                  <small className="text-muted">Email cannot be changed for security reasons.</small>
                </div>

                <hr className="my-4" />
                
                {/* Password Section */}
                <h6 className="fw-bold mb-3 text-danger"><FaLock className="me-2" /> Change Password</h6>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">New Password</label>
                    <input 
                      type="password" 
                      className="form-control" 
                      value={settingsForm.password} 
                      onChange={(e) => setSettingsForm({ ...settingsForm, password: e.target.value })} 
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Confirm Password</label>
                    <input 
                      type="password" 
                      className="form-control" 
                      value={settingsForm.confirmPassword} 
                      onChange={(e) => setSettingsForm({ ...settingsForm, confirmPassword: e.target.value })} 
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button type="button" className="btn btn-light me-2">Cancel</button>
                  <button type="submit" className="btn btn-primary px-4 fw-bold">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ==========================================
  // 6. MAIN RENDER
  // ==========================================

  return (
    <div className="admin-container super-admin-dashboard">

      {/* --- Mobile Overlay --- */}
      {showSidebar && (
        <div className="sidebar-overlay" onClick={() => setShowSidebar(false)}></div>
      )}

      {/* --- Sidebar Navigation --- */}
      <div className={`admin-sidebar ${showSidebar ? 'show' : ''}`}>
        <div className="d-flex justify-content-between align-items-center pe-3">
          <div className="sidebar-logo">
            DDoctor<span className="text-primary">.Admin</span>
          </div>
          {/* Mobile Close Button */}
          <div className="d-lg-none text-white cursor-pointer" onClick={() => setShowSidebar(false)}>
            <FaTimes size={20} />
          </div>
        </div>
        
        <div className="mt-4 d-flex flex-column gap-1">
          <div 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} 
            onClick={() => handleNavClick('dashboard')}
          >
            <FaChartLine className="nav-icon" /> Dashboard
          </div>
          <div 
            className={`nav-item ${activeTab === 'doctors' ? 'active' : ''}`} 
            onClick={() => handleNavClick('doctors')}
          >
            <FaUserMd className="nav-icon" /> Doctors
          </div>
          <div 
            className={`nav-item ${activeTab === 'hospitals' ? 'active' : ''}`} 
            onClick={() => handleNavClick('hospitals')}
          >
            <FaHospital className="nav-icon" /> Hospitals
          </div>
          <div 
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`} 
            onClick={() => handleNavClick('users')}
          >
            <FaUsers className="nav-icon" /> Users / Patients
          </div>
          <div 
            className={`nav-item ${activeTab === 'audit' ? 'active' : ''}`} 
            onClick={() => handleNavClick('audit')}
          >
            <FaShieldAlt className="nav-icon" /> Audit Logs
          </div>
          <div 
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`} 
            onClick={() => handleNavClick('settings')}
          >
            <FaCog className="nav-icon" /> Settings
          </div>
        </div>

        <div className="mt-auto mb-4">
          <div className="nav-item text-danger" onClick={handleLogout}>
            <FaSignOutAlt className="nav-icon" /> Logout
          </div>
        </div>
      </div>

      {/* --- Main Content Area --- */}
      <div className="admin-main">

        {/* --- Top Header --- */}
        <div className="admin-header">
          <div className="d-flex align-items-center">
            {/* Mobile Hamburger Toggle */}
            <div className="mobile-toggle-btn me-3" onClick={() => setShowSidebar(true)}>
              <FaBars />
            </div>

            <h5 className="mb-0 fw-bold text-dark me-3">
              {activeTab === 'dashboard' ? 'System Overview' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </h5>
            
            <span className="badge bg-success bg-opacity-10 text-success border border-success px-2 rounded-pill d-none d-md-inline-block" style={{ fontSize: '10px' }}>
              Production Env
            </span>
          </div>

          <div className="d-flex align-items-center gap-3">
            
            {/* Notification Dropdown */}
            <div className="position-relative cursor-pointer" ref={notifRef}>
              <div onClick={() => setShowNotif(!showNotif)} className="text-secondary position-relative">
                <FaBell size={20} />
                {stats?.notifications?.length > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '8px' }}>
                    {stats.notifications.length}
                  </span>
                )}
              </div>
              
              {showNotif && (
                <div className="dropdown-menu-custom shadow p-0">
                  <div className="p-2 border-bottom fw-bold bg-light small text-uppercase">Notifications</div>
                  <div className="notif-list">
                    {stats?.notifications?.length > 0 ? stats.notifications.map(n => (
                      <div key={n.id} className="p-3 border-bottom notif-item">
                        <div className="d-flex align-items-start">
                          {n.type === 'success' 
                            ? <FaCheckCircle className="text-success mt-1 me-2" /> 
                            : <FaExclamationTriangle className="text-warning mt-1 me-2" />
                          }
                          <div>
                            <div className="fw-bold small text-dark">{n.title}</div>
                            <div className="text-muted" style={{ fontSize: '11px' }}>{n.text}</div>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="p-3 text-center small text-muted">No new alerts</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="border-start h-50 mx-1"></div>

            {/* Profile Dropdown */}
            <div className="position-relative" ref={profileRef}>
              <div className="d-flex align-items-center cursor-pointer" onClick={() => setShowProfile(!showProfile)}>
                <div className="text-end me-2 lh-1 d-none d-md-block">
                  <div className="fw-bold text-dark" style={{ fontSize: '14px' }}>{adminName}</div>
                  <small className="text-muted" style={{ fontSize: '10px' }}>Super Admin</small>
                </div>
                <FaUserCircle size={32} className="text-primary opacity-75" />
              </div>

              {showProfile && (
                <div className="dropdown-menu-custom shadow p-2" style={{ right: 0, width: '180px' }}>
                  <div className="dropdown-item-custom" onClick={() => { handleNavClick('settings'); setShowProfile(false); }}>
                    <FaCog className="me-2 text-muted" /> Settings
                  </div>
                  <div className="dropdown-divider my-1 border-top"></div>
                  <div className="dropdown-item-custom text-danger" onClick={handleLogout}>
                    <FaSignOutAlt className="me-2" /> Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* --- Content Renderer --- */}
        <div className="admin-content">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center h-50">
              <div className="spinner-border text-primary" role="status"></div>
              <span className="ms-2 fw-bold text-muted">Loading Data...</span>
            </div>
          ) : (
            <>
              {activeTab === 'dashboard' && <DashboardHome />}
              
              {/* Tables */}
              {activeTab === 'users' && <DataTable data={users.filter(u => u.role === 'patient')} type="Patients" />}
              {activeTab === 'doctors' && <DataTable data={users.filter(u => u.role === 'doctor')} type="Doctors" />}
              {activeTab === 'hospitals' && <DataTable data={users.filter(u => u.role === 'hospital')} type="Hospitals" />}
              
              {/* Settings */}
              {activeTab === 'settings' && <SettingsView />}
              
              {/* Audit Logs */}
              {activeTab === 'audit' && (
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white py-3">
                    <h5 className="mb-0 fw-bold">System Security Logs</h5>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="ps-4">Date & Time</th>
                          <th>Admin Name</th>
                          <th>Action Type</th>
                          <th>Target / Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats?.recentLogs?.length > 0 ? stats.recentLogs.map((log, index) => (
                          <tr key={index}>
                            <td className="ps-4 text-muted small">
                              {new Date(log.date).toLocaleString()}
                            </td>
                            <td className="fw-bold">{log.adminName}</td>
                            <td>
                              <span className={`badge ${log.action === 'BAN_USER' ? 'bg-danger' :
                                log.action === 'VERIFY_DOCTOR' ? 'bg-success' :
                                  'bg-primary'
                                }`}>
                                {log.action}
                              </span>
                            </td>
                            <td>
                              <div className="text-dark small">{log.target}</div>
                              <div className="text-muted" style={{ fontSize: '11px' }}>{log.details}</div>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="4" className="text-center py-5 text-muted">No logs recorded yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* --- Footer --- */}
        <footer className="bg-white border-top py-2 px-4 d-flex justify-content-between align-items-center mt-auto shadow-sm">
          <span className="text-muted small">© 2026 DDoctor.pk - All Rights Reserved.</span>
          <div className="d-none d-md-flex gap-3">
            <span className="text-muted small">Admin Console <span className="fw-bold text-dark">v1.0.0</span></span>
            <span className="text-muted small">System: <span className="text-success fw-bold">Online</span></span>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default SuperAdminDashboard;